import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, HardHat } from "lucide-react";
import { IMPRESSUM, DATENSCHUTZ } from "@/content/legal";
import { LegalFooter } from "@/components/LegalFooter";

// Einzelne Zeilenumbrüche als eigene Absätze darstellen; Zeilen mit zwei Leerzeichen am Ende bleiben Zeilenumbrüche.
const absaetze = (md) => md.split("\n").map((z, i, a) => (i === a.length - 1 ? z : z + (z.endsWith("  ") ? "\n" : "\n\n"))).join("");

const MD = {
  h1: (p) => <h1 className="text-3xl sm:text-4xl font-head font-extrabold tracking-tight text-slate-900 mb-2" {...p} />,
  h2: (p) => <h2 className="text-lg md:text-xl font-head font-bold text-slate-900 mt-8 mb-2" {...p} />,
  h3: (p) => <h3 className="text-base md:text-lg font-head font-semibold text-slate-900 mt-6 mb-2" {...p} />,
  p: (p) => <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-3" {...p} />,
  ul: (p) => <ul className="list-disc pl-6 space-y-1 mb-3 text-sm sm:text-base text-slate-700" {...p} />,
  ol: (p) => <ol className="list-decimal pl-6 space-y-1 mb-3 text-sm sm:text-base text-slate-700" {...p} />,
  blockquote: (p) => <blockquote className="border-l-4 border-primary bg-orange-50/60 rounded-r-lg px-4 py-3 my-4 [&>p]:mb-0" {...p} />,
  strong: (p) => <strong className="font-semibold text-slate-900" {...p} />,
  a: ({ href, children }) => (
    <a href={href} className="text-primary font-medium underline underline-offset-2 hover:no-underline break-words"
      {...(href?.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{children}</a>
  ),
};

export default function Legal({ page }) {
  const text = page === "impressum" ? IMPRESSUM : DATENSCHUTZ;
  useEffect(() => {
    document.title = `${page === "impressum" ? "Impressum" : "Datenschutz"} · PrüfDoc Pro`;
    window.scrollTo(0, 0);
  }, [page]);
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid={`legal-page-${page}`}>
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-logo-link">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center"><HardHat className="h-5 w-5 text-white" /></div>
            <span className="text-lg font-head font-extrabold">PrüfDoc Pro</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-primary transition-colors" data-testid="legal-back-link">
            <ArrowLeft className="h-4 w-4" />Zurück
          </Link>
        </div>
      </header>
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <article className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-10 shadow-sm" data-testid="legal-content">
          <ReactMarkdown components={MD}>{absaetze(text)}</ReactMarkdown>
        </article>
        <Link to="/" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline" data-testid="legal-back-link-bottom">
          <ArrowLeft className="h-4 w-4" />Zurück zur Startseite bzw. zur Anmeldung
        </Link>
      </main>
      <LegalFooter className="pb-6" />
    </div>
  );
}
